import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  FormControl,
  Grid,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import MenuItem from "@material-ui/core/MenuItem";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray, capitalize } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import { head } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

const ScheduleSchema = Yup.object().shape({
  title: Yup.string().min(2, "Obrigatório").required("Obrigatório"),
  body: Yup.string().min(5, "Mensagem muito curta").required("Obrigatório"),
  contactIds: Yup.array().min(1, "Obrigatório"),
  sendAt: Yup.string().required("Obrigatório"),
  whatsappId: Yup.number().nullable().required("Obrigatório"),
  recurrenceType: Yup.string().required(),
  recurrenceEndDate: Yup.string()
    .nullable()
    .when("recurrenceType", {
      is: (value) => value && value !== "none",
      then: (schema) => schema.required("Obrigatório"),
      otherwise: (schema) => schema.nullable(),
    }),
  ignoreWeekends: Yup.boolean(),
});

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");
const isProbablyPhone = (v) => {
  const d = digitsOnly(v);
  return d.length >= 10;
};

const filter = createFilterOptions({
  trim: true,
});

const isRealContactOption = (opt) => {
  if (!opt) return false;
  if (typeof opt === "string") return false;
  return !opt.kind && opt.id !== undefined && opt.id !== null;
};

const normalizePhoneBR = (digits) => {
  const d = digitsOnly(digits);
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) {
    return d.slice(2);
  }
  return d;
};

const phonesEqual = (a, b) => {
  const na = normalizePhoneBR(a);
  const nb = normalizePhoneBR(b);
  return na !== "" && nb !== "" && na === nb;
};

const normalizeName = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  ticketId,
  defaultWhatsappId,
  cleanContact,
  reload,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const initialState = {
    title: "",
    body: "",
    contactIds: [],
    sendAt: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
    sentAt: "",
    recurrenceType: "none",
    recurrenceEndDate: "",
    ignoreWeekends: false,
    ticketId: ticketId ?? null,
    whatsappId:
      defaultWhatsappId !== undefined && defaultWhatsappId !== null
        ? Number(defaultWhatsappId)
        : null,
    status: "PENDENTE",
  };

  const [schedule, setSchedule] = useState(initialState);
  const [currentContacts, setCurrentContacts] = useState([]);
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [whatsapps, setWhatsapps] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const messageInputRef = useRef();

  useEffect(() => {
    const { companyId } = user;
    if (open) {
      try {
        (async () => {
          const [contactResponse, whatsappResponse] = await Promise.all([
            api.get("/contacts/list", {
              params: { companyId: companyId },
            }),
            api.get("/whatsapp", {
              params: { companyId: companyId, session: 0 },
            }),
          ]);
          const contactList = contactResponse.data;
          const whatsappList = whatsappResponse.data || [];
          setWhatsapps(whatsappList);
          let customList = contactList.map((c) => ({ id: c.id, name: c.name }));
          if (isArray(customList)) {
            setOptions(customList);
          }
          if (contactId) {
            setSchedule((prev) => ({ ...prev, contactIds: [contactId] }));
            const c = customList.find((ct) => ct.id === contactId);
            if (c) {
              setCurrentContacts([c]);
              setOptions((prev) => {
                if (prev.some((opt) => opt.id === c.id)) return prev;
                return [...prev, c];
              });
            }
          }

          if (!scheduleId) {
            setSchedule((prevState) => ({
              ...prevState,
              ticketId: ticketId ?? null,
              whatsappId:
                defaultWhatsappId !== undefined && defaultWhatsappId !== null
                  ? Number(defaultWhatsappId)
                  : prevState.whatsappId,
            }));
          }

          if (!scheduleId) return;

          const { data } = await api.get(`/schedules/${scheduleId}`);
          setSchedule((prevState) => {
            return {
              ...prevState,
              ...data,
              contactIds: data.contacts ? data.contacts.map((c) => c.id) : [],
              sendAt: moment(data.sendAt).format("YYYY-MM-DDTHH:mm"),
              recurrenceEndDate: data.recurrenceEndDate
                ? moment(data.recurrenceEndDate).format("YYYY-MM-DDTHH:mm")
                : "",
              whatsappId: data.whatsappId ?? null,
              ticketId: data.ticketId ?? null,
            };
          });
          if (data.contacts && data.contacts.length > 0) {
            setOptions((prev) => {
              const existingIds = new Set(prev.map((opt) => opt.id));
              const merged = [...prev];
              data.contacts.forEach((contact) => {
                if (!existingIds.has(contact.id)) {
                  merged.push(contact);
                  existingIds.add(contact.id);
                }
              });
              return merged;
            });
          }
        })();
      } catch (err) {
        toastError(err);
      }
    }
  }, [scheduleId, contactId, open, user, ticketId, defaultWhatsappId]);

  useEffect(() => {
    if (!open || searchParam.length < 3) {
      setLoadingContacts(false);
      return;
    }
    setLoadingContacts(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoadingContacts(false);
        } catch (err) {
          setLoadingContacts(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, open]);

  const handleClose = () => {
    onClose();
    setAttachment(null);
    setSchedule(initialState);
    setCurrentContacts([]);
    setSearchParam("");
    setOptions([]);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveSchedule = async (values) => {
    const scheduleData = { ...values, userId: user.id };
    scheduleData.whatsappId = scheduleData.whatsappId
      ? Number(scheduleData.whatsappId)
      : null;
    scheduleData.ticketId = scheduleData.ticketId
      ? Number(scheduleData.ticketId)
      : null;
    if (scheduleData.recurrenceType === "none") {
      scheduleData.recurrenceEndDate = null;
    } else if (scheduleData.recurrenceEndDate) {
      scheduleData.recurrenceEndDate = moment(
        scheduleData.recurrenceEndDate,
      ).toISOString();
    }
    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${scheduleId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/schedules", scheduleData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("scheduleModal.success"));
      if (typeof reload == "function") {
        reload();
      }
      if (contactId) {
        if (typeof cleanContact === "function") {
          cleanContact();
          history.push("/schedules");
        }
      }
    } catch (err) {
      toastError(err);
    }
    setCurrentContacts([]);
    setSchedule(initialState);
    handleClose();
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("body", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const renderContactLabel = (contact) => {
    const contactNickname = contact?.nickname ? ` (${contact.nickname})` : "";
    const contactNumber = contact?.number ? ` - ${contact.number}` : "";
    return `${contact.name}${contactNickname}${contactNumber}`;
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);

    const input = String(params.inputValue || "").trim();
    const digits = digitsOnly(input);
    const inputLooksPhone = isProbablyPhone(input);

    if (input === "" || loadingContacts || input.length < 3) return filtered;

    const hasAnyRealMatch = filtered.some(isRealContactOption);

    let hasExactMatch = false;

    if (hasAnyRealMatch) {
      if (inputLooksPhone) {
        hasExactMatch = filtered.some(
          (opt) => isRealContactOption(opt) && phonesEqual(opt.number, digits),
        );
      } else {
        const n = normalizeName(input);
        hasExactMatch = filtered.some(
          (opt) => isRealContactOption(opt) && normalizeName(opt.name) === n,
        );
      }
    }

    if (!hasExactMatch) {
      filtered.push({
        kind: "add",
        name: input,
        inputValue: input,
      });

      if (inputLooksPhone) {
        filtered.push({
          kind: "send",
          name: input,
          inputValue: input,
          number: digits,
        });
      }
    }
    return filtered;
  };

  const renderOption = (option) => {
    if (option.number && !option.kind) {
      return (
        <Typography
          component="span"
          style={{
            fontSize: 14,
            marginLeft: "10px",
            display: "inline-flex",
            alignItems: "center",
            lineHeight: "2",
          }}
        >
          {renderContactLabel(option)}
        </Typography>
      );
    }

    if (option.kind === "add") {
      return `${i18n.t("newTicketModal.add")} ${option.inputValue}`;
    }

    if (option.kind === "send") {
      return `Enviar para ${option.inputValue}`;
    }

    return option.name || "";
  };

  const renderOptionLabel = (option) => {
    if (typeof option === "string") return option;
    if (option.name && !option.kind) return renderContactLabel(option);
    return option.inputValue || option.name || "";
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (schedule.mediaPath) {
      await api.delete(`/schedules/${schedule.id}/media-upload`);
      setSchedule((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success(i18n.t("scheduleModal.toasts.deleted"));
      if (typeof reload == "function") {
        console.log(reload);
        console.log("1");
        reload();
      }
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("scheduleModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {schedule.status === "ERRO"
            ? "Erro de Envio"
            : `Mensagem ${capitalize(schedule.status)}`}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={schedule}
          enableReinitialize={true}
          validationSchema={ScheduleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveSchedule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2} alignItems="stretch">
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("scheduleModal.form.title")}
                      name="title"
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl variant="outlined" fullWidth>
                      <Autocomplete
                        multiple
                        fullWidth
                        value={currentContacts}
                        options={options}
                        loading={loadingContacts}
                        clearOnBlur={false}
                        autoHighlight
                        freeSolo
                        clearOnEscape
                        getOptionLabel={renderOptionLabel}
                        renderOption={renderOption}
                        filterOptions={createAddContactOption}
                        inputValue={searchParam}
                        onInputChange={(event, newInputValue) => {
                          setSearchParam(newInputValue);
                        }}
                        onChange={(e, contactsSel) => {
                          const realContacts = (contactsSel || []).filter(
                            isRealContactOption,
                          );
                          const ids = realContacts.map((c) => c.id);
                          setCurrentContacts(realContacts);
                          setFieldValue("contactIds", ids);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label={i18n.t("newTicketModal.fieldLabel")}
                            placeholder={i18n.t("newTicketModal.fieldLabel")}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loadingContacts ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={9} style={{ display: "flex" }}>
                    <Field
                      as={TextField}
                      rows={5}
                      multiline={true}
                      label={i18n.t("scheduleModal.form.body")}
                      name="body"
                      inputRef={messageInputRef}
                      error={touched.body && Boolean(errors.body)}
                      helperText={touched.body && errors.body}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      style={{ flex: 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3} style={{ display: "flex" }}>
                    <div style={{ width: "100%", height: "100%" }}>
                      <MessageVariablesPicker
                        disabled={isSubmitting}
                        onClick={(value) =>
                          handleClickMsgVar(value, setFieldValue)
                        }
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("scheduleModal.form.sendAt")}
                      type="datetime-local"
                      name="sendAt"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={touched.sendAt && Boolean(errors.sendAt)}
                      helperText={touched.sendAt && errors.sendAt}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label={i18n.t("scheduleModal.form.connection")}
                      value={values.whatsappId ?? ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        setFieldValue(
                          "whatsappId",
                          value === "" ? null : value,
                        );
                      }}
                      variant="outlined"
                      fullWidth
                      error={touched.whatsappId && Boolean(errors.whatsappId)}
                      helperText={touched.whatsappId && errors.whatsappId}
                    >
                      <MenuItem value="" disabled>
                        {i18n.t("scheduleModal.form.connectionPlaceholder")}
                      </MenuItem>
                      {whatsapps.map((whatsapp) => (
                        <MenuItem key={whatsapp.id} value={whatsapp.id}>
                          {`${whatsapp.name} (${whatsapp.status})`}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      select
                      label={i18n.t("scheduleModal.form.recurrenceType")}
                      name="recurrenceType"
                      variant="outlined"
                      fullWidth
                      onChange={(event) => {
                        const value = event.target.value;
                        setFieldValue("recurrenceType", value);
                        if (value === "none") {
                          setFieldValue("recurrenceEndDate", "");
                          setFieldValue("ignoreWeekends", false);
                        }
                      }}
                    >
                      <MenuItem value="none">
                        {i18n.t("scheduleModal.recurrence.none")}
                      </MenuItem>
                      <MenuItem value="daily">
                        {i18n.t("scheduleModal.recurrence.daily")}
                      </MenuItem>
                      <MenuItem value="weekly">
                        {i18n.t("scheduleModal.recurrence.weekly")}
                      </MenuItem>
                      <MenuItem value="biweekly">
                        {i18n.t("scheduleModal.recurrence.biweekly")}
                      </MenuItem>
                      <MenuItem value="monthly">
                        {i18n.t("scheduleModal.recurrence.monthly")}
                      </MenuItem>
                      <MenuItem value="quarterly">
                        {i18n.t("scheduleModal.recurrence.quarterly")}
                      </MenuItem>
                      <MenuItem value="semiannual">
                        {i18n.t("scheduleModal.recurrence.semiannual")}
                      </MenuItem>
                      <MenuItem value="annual">
                        {i18n.t("scheduleModal.recurrence.annual")}
                      </MenuItem>
                    </Field>
                  </Grid>

                  {values.recurrenceType !== "none" && (
                    <Grid item xs={12} md={6}>
                      <Field
                        as={TextField}
                        label={i18n.t("scheduleModal.form.recurrenceEndDate")}
                        type="datetime-local"
                        name="recurrenceEndDate"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={
                          touched.recurrenceEndDate &&
                          Boolean(errors.recurrenceEndDate)
                        }
                        helperText={
                          touched.recurrenceEndDate && errors.recurrenceEndDate
                        }
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          name="ignoreWeekends"
                          color="primary"
                          disabled={values.recurrenceType === "none"}
                        />
                      }
                      label={i18n.t("scheduleModal.form.ignoreWeekends")}
                      disabled={values.recurrenceType === "none"}
                    />
                  </Grid>

                  {(schedule.mediaPath || attachment) && (
                    <Grid item xs={12}>
                      <Button startIcon={<AttachFile />}>
                        {attachment ? attachment.name : schedule.mediaName}
                      </Button>
                      <IconButton
                        onClick={() => setConfirmationOpen(true)}
                        color="secondary"
                      >
                        <DeleteOutline color="secondary" />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                {!attachment && !schedule.mediaPath && (
                  <Button
                    color="primary"
                    onClick={() => attachmentFile.current.click()}
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("quickMessages.buttons.attach")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("scheduleModal.buttons.cancel")}
                </Button>
                {(schedule.sentAt === null || schedule.sentAt === "") && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {scheduleId
                      ? `${i18n.t("scheduleModal.buttons.okEdit")}`
                      : `${i18n.t("scheduleModal.buttons.okAdd")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ScheduleModal;
